<template>
	<span :class="classes">
		<!-- @deprecated: use #left-icon -->
		<slot name="leftIcon"  v-if="$slots.leftIcon || leftIcon">
			<FontAwesomeIcon
				v-if="leftIcon"
				:icon="leftIcon"
				class="teaming-textbox__leftIcon"
			/>
		</slot>
		<span class="teaming-textbox__leftIcon" v-if="$slots[ `left-icon` ]">
			<slot name="left-icon"></slot>
		</span>
		<span
			v-if="isReadonly"
			class="teaming-textbox__input teaming-textbox__input--read-only"
		>
			{{ value }}
		</span>
		<input
			v-else
			:class="inputClasses"
			type="text"
			:disabled="disabled"
			:name="name"
			:placeholder="placeholder"
			:value="value"
			ref="inputField"
			@input="onDebounceInput"
			@focus="onFocus"
			@blur="onBlur"
			@keydown="onKeyDown"
			v-on:keyup.enter="onEnter"
			v-on:keyup.escape="onEscape"
		/>
		<span class="teaming-textbox__rightIcon" v-if="$slots[ `right-icon` ]">
			<slot name="right-icon"></slot>
		</span>
		<!-- @deprecated: use #right-icon -->
		<slot name="rightIcon" v-if="$slots.rightIcon || rightIcon">
			<FontAwesomeIcon
				v-if="rightIcon"
				:icon="rightIcon"
				class="teaming-textbox__rightIcon"
			/>
		</slot>
		<FontAwesomeIcon
			v-if="!isValid"
			:icon="[ 'fas', 'exclamation-circle' ]"
			class="teaming-textbox__validation-error-icon"
		/>
	</span>
</template>

<script>
import debounce from "lodash/debounce";
import * as KeyCode from "keycode-js";

import { Enum } from "@teaming/common-enums";

export const TEAMING_TEXTBOX_SIZE = Enum.create( {
	SMALL: "SMALL",
	MEDIUM: "MEDIUM",
} );

export default {
	name: "TeamingTextbox",
	teamingPackage: "common-ui",
	emits: [
		"input",
		"blur",
		"focus",
		"enterKey",
		"escapeKey",
		"keydown",
		"paste",
	],
	data() {
		return {
			hasPasted: false,
			isFocused: false,
			onDebounceInput: null,
		};
	},
	props: {
		name: {
			type: String,
			required: true,
		},
		value: {
			type: String,
			default: "",
		},
		isReadonly: {
			type: Boolean,
			default: false,
		},
		placeholder: {
			type: String,
			default: "",
		},
		borderless: {
			type: Boolean,
			default: false,
		},
		isValid: {
			type: Boolean,
			default: true,
		},
		disabled: {
			type: Boolean,
			default: false,
		},
		/** @deprecated: use #left-icon slot */
		leftIcon: {
			type: Array,
			default: () => undefined,
		},
		/** @deprecated: use #right-icon slot */
		rightIcon: {
			type: Array,
			default: () => undefined,
		},
		focus: {
			type: Boolean,
			default: false,
		},
		selection: {
			type: Array,
			default: () => [],
		},
		size: {
			type: String,
			default: TEAMING_TEXTBOX_SIZE.MEDIUM,
			validator: value => TEAMING_TEXTBOX_SIZE.includes( value ),
		},
		debounceInputDuration: {
			type: Number,
			default: 0,
		},
	},
	computed: {
		classes() {
			const { size, } = this;
			return {
				"teaming-textbox": true,
				"teaming-textbox--sm": ( size === TEAMING_TEXTBOX_SIZE.SMALL ),
				"teaming-textbox--md": ( size === TEAMING_TEXTBOX_SIZE.MEDIUM ),
				"teaming-textbox--borderless": this.borderless,
				"teaming-textbox--invalid": !this.isValid,
				"teaming-textbox--disabled": this.disabled,
				"teaming-textbox--focused--valid": this.isValid && this.isFocused,
				"teaming-textbox--focused--invalid": !this.isValid && this.isFocused,
				"teaming-textbox--readonly": this.isReadonly,
			};
		},
		inputClasses() {
			const { size, } = this;
			return {
				"teaming-textbox__input": true,
				"teaming-textbox__input--sm": ( size === TEAMING_TEXTBOX_SIZE.SMALL ),
				"teaming-textbox__input--md": ( size === TEAMING_TEXTBOX_SIZE.MEDIUM ),
			};
		},
	},
	watch: {
		async focus( isFocused, wasFocused ) {
			if ( isFocused && !wasFocused ) {
				await this.$nextTick();
				this.tryFocus();
			}
		},
	},
	methods: {
		onBlur( e ) {
			this.isFocused = false;
			this.$emit( "blur", e );
		},
		onKeyDown( e ) {
			this.$emit( "keydown", e );
			const { ctrlKey, metaKey, code, } = e;
			if ( ( ctrlKey || metaKey ) && code === KeyCode.CODE_V ) {
				this.hasPasted = true;
			}
		},
		onEnter( e ) {
			this.$emit( "enterKey", e );
		},
		onEscape( e ) {
			this.$emit( "escapeKey", e );
		},
		onFocus( e ) {
			this.isFocused = true;
			this.$emit( "focus", e );
		},
		onTextInput( e ) {
			if ( this.isReadonly ) {
				e.preventDefault();
				e.stopPropagation();
				return;
			}
			this.$emit( "input", e );
			if ( this.hasPasted ) {
				this.$emit( "paste", e );
				this.hasPasted = false;
			}
		},
		tryFocus() {
			this.$refs.inputField.focus();
		},
	},
	async created() {
		const { debounceInputDuration, } = this;
		this.onDebounceInput = debounce( e => {
			this.onTextInput( e );
		}, debounceInputDuration );
	},
	async mounted() {
		await this.$nextTick();
		if ( this.focus ) {
			this.tryFocus();
		}

		// @see https://developer.mozilla.org/en-US/docs/Web/API/HTMLInputElement/setSelectionRange
		if ( this.selection.length ) {
			if ( this.selection.length === 1 ) {
				const [ start, ] = this.selection;
				this.$refs.inputField.setSelectionRange( start, start );
			}
			if ( this.selection.length === 2 ) {
				const [ start, end, ] = this.selection;
				this.$refs.inputField.setSelectionRange( start, end );
			}
		}
	},
};
</script>

<style lang="scss" scoped>
.teaming-textbox {
	display: inline-flex;
	align-items: center;

	border: 0.0625rem solid $textbox-border-color;
	background-color: $textbox-background-color;

	&:not(.teaming-textbox--borderless):not(.teaming-textbox--disabled):hover {
		border: 0.0625rem solid $textbox-border-color--hover;
	}
}

.teaming-textbox--sm {
	border-radius: 0.25rem;
	@include font-and-line-for(-1);
}

.teaming-textbox--md {
	border-radius: 0.5rem;
	@include font-and-line-for(0);
}

.teaming-textbox--focused--invalid {
	border: 0.0625rem solid $textbox-border-color--invalid--hover;
	box-shadow: 0 0 0 .1875rem $textbox-box-shadow--invalid;
}

.teaming-textbox--focused--valid {
	border: 0.0625rem solid $textbox-border-color--hover;
	box-shadow: 0 0 0 .1875rem $textbox-box-shadow--active;
}

.teaming-textbox__input {
	color: $textbox-color;
	background-color: transparent;
	width: 100%;
	outline: none;
	border: none;
	border-radius: 0;

	&::placeholder {
		color: $textbox-placeholder-font-color;
	}
}

.teaming-textbox__input--sm {
	padding: 0.2rem 0.625rem;
}

.teaming-textbox__input--md {
	padding: 0.625rem 0.75rem;
}

.teaming-textbox__input--read-only {
	-webkit-user-select: none; /* Safari */
	-moz-user-select: none; /* Firefox */
	-ms-user-select: none; /* IE10+/Edge */
	user-select: none; /* Standard */
}

.teaming-textbox--borderless {
	border: none !important;
	box-shadow: none !important;

	.teaming-textbox__input {
		padding: 0;
		margin: 0;
	}
}

.teaming-textbox--invalid {
	border-color: $textbox-border-color--invalid;
	background-color: $textbox-background-color--invalid;

	&:hover {
		border: 0.0625rem solid $textbox-border-color--invalid--hover;
	}
}

.teaming-textbox--disabled {
	background-color: $textbox-background-color--disabled;
	border: 0.0625rem solid $textbox-border-color--disabled;
	cursor: not-allowed;

	&:hover {
		border: 0.0625rem solid $textbox-border-color--disabled;
	}

	.teaming-textbox__input {
		//
		// Get around problem rendering input text color in iOS Safari.
		// @see: https://stackoverflow.com/questions/30102792/css-media-query-to-target-only-ios-devices/47818418#47818418
		//
		@supports not (-webkit-touch-callout: none) {
			/* CSS for other than iOS devices */
			color: $textbox-color--disabled;
		}
	}

	.teaming-textbox__leftIcon {
		color: $textbox-icon-color--disabled;
	}

	.teaming-textbox__rightIcon {
		color: $textbox-icon-color--disabled;
	}
}

.teaming-textbox--readonly {
	background-color: transparent;

	&:hover {
		background-color: transparent;
	}
}

.teaming-textbox__validation-error-icon {
	margin-right: .75rem;
	color: $textbox-validation-error-icon-color;
}

.teaming-textbox__leftIcon {
	color: $textbox-icon-color;
}

.teaming-textbox--sm .teaming-textbox__leftIcon {
	margin-left: 0.625rem;
}

.teaming-textbox--md .teaming-textbox__leftIcon {
	margin-left: 0.75rem;
}

.teaming-textbox__rightIcon {
	color: $textbox-icon-color;
}

.teaming-textbox--sm .teaming-textbox__rightIcon {
	margin-right: 0.625rem;
}

.teaming-textbox--md .teaming-textbox__rightIcon {
	margin-right: 0.75rem;
}
</style>
